import React, { useState, useEffect, useRef } from 'react';
import MapFrame from '../../components/MapFrame';
import { createPortal } from 'react-dom';
import RSVPForm from '../../components/RSVPForm';
import Wishlist from '../../components/Wishlist';
import YandexMap from '../../components/YandexMap';
import Countdown from '../../components/Countdown';
import TickerBar from '../../components/TickerBar';
import FixedCTA from '../../components/FixedCTA';
import useReveal from '../../components/useReveal';
import Schedule from '../../components/Schedule';
import DresscodePalette from '../../components/DresscodePalette';
import DateCards from '../../components/DateCards';
import CalendarButton from '../../components/CalendarButton';
import InvitationLetter from '../../components/InvitationLetter';
import BlockSubtitle from '../../components/BlockSubtitle';
import PaidWatermark from '../../components/PaidWatermark';
import MusicPlayer from '../../components/MusicPlayer';
import './Amethyst.css';

/* Shared-component class maps in Amethyst's naming. */
const AMT_RSVP_CLASSES = {
  form:    'amt-form',
  tabs:    'amt-rsvp-tabs',
  tab:     'amt-rsvp-tab',
  tabSel:  'amt-rsvp-tab--sel',
  field:   'amt-form-field',
  label:   'amt-form-lbl',
  input:   'amt-form-input',
  submit:  'amt-btn amt-btn--primary amt-btn--full amt-btn--submit',
  success: 'amt-form-ok',
  error:   'amt-form-err',
  chip:    'amt-chip',
  chipSel: 'amt-chip--sel',
  chips:   'amt-form-chips',
};
const AMT_WISHLIST_CLASSES = {
  list:        'amt-wish',
  item:        'amt-wish-item',
  itemBorder:  'amt-wish-item--border',
  row:         'amt-wish-row',
  name:        'amt-wish-name',
  price:       'amt-wish-price',
  btn:         'amt-btn amt-btn--outline',
  btnReserved: 'amt-btn--res',
};
const AMT_WISHLIST_MODAL_CLASSES = {
  bg:         'amt-wish-modal-bg',
  modal:      'amt-wish-modal',
  title:      'amt-wish-modal-title',
  subtitle:   'amt-wish-modal-sub',
  input:      'amt-form-input',
  error:      'amt-form-err',
  btns:       'amt-wish-modal-btns',
  btnCancel:  'amt-btn amt-btn--outline',
  btnConfirm: 'amt-btn amt-btn--primary',
};
const AMT_CD_CLASSES     = { root: 'amt-cd',      cell: 'amt-cd-cell',     num: 'amt-cd-num',     label: 'amt-cd-lbl' };
const AMT_TICKER_CLASSES = { bar:  'amt-ticker-bar', track: 'amt-ticker-track', item: 'amt-ticker-item', sep: 'amt-ticker-sep' };
const AMT_SCHED_CLASSES  = { root: 'amt-sched', row: 'amt-sched-row', rowBorder: 'amt-sched-row--border', time: 'amt-sched-time', info: 'amt-sched-info', name: 'amt-sched-name', desc: 'amt-sched-desc' };
const AMT_DRESS_CLASSES  = { wrap: 'amt-swatches', swatchWrap: 'amt-swatch-wrap', swatch: 'amt-swatch', swatchLabel: 'amt-swatch-lbl', photos: 'amt-dc-photos', photo: 'amt-dc-photo' };
const AMT_DATECARDS_CLASSES = { row: 'amt-date-row', card: 'amt-day-card', cardOn: 'amt-day-card--on', num: 'amt-day-num', mon: 'amt-day-mon' };

/* ── Helpers ─────────────────────────────────────────────────── */
const src = u => !u ? null : u.startsWith('http') ? u : `${u}`;
const FILTER_CSS = {
  warm: 'sepia(0.3) saturate(1.3) brightness(1.05)',
  cool: 'hue-rotate(15deg) saturate(0.9) brightness(1.05)',
  vintage: 'sepia(0.5) contrast(0.9) brightness(1.1) saturate(0.8)',
  bw: 'grayscale(1)',
  dramatic: 'contrast(1.3) brightness(0.9) saturate(1.2)',
  soft: 'brightness(1.1) saturate(0.8) contrast(0.9)',
  golden: 'sepia(0.4) brightness(1.1) saturate(1.4) hue-rotate(-10deg)',
};
const flt = p => (p?.filter && p.filter !== 'none' ? FILTER_CSS[p.filter] : '') || undefined;
const objPos = p => (p && Number.isFinite(p.focalX) && Number.isFinite(p.focalY))
  ? `${(p.focalX * 100).toFixed(1)}% ${(p.focalY * 100).toFixed(1)}%`
  : undefined;

/* formatPrice is now provided by the shared Wishlist component. */

const MON_FULL  = ['января','февраля','марта','апреля','мая','июня','июля','августа','сентября','октября','ноября','декабря'];
const MON_SHORT = ['янв','фев','мар','апр','мая','июн','июл','авг','сен','окт','ноя','дек'];

const fmtDateDots = raw => {
  if (!raw) return '??.??.????';
  const d = new Date(raw);
  return [d.getDate(), d.getMonth() + 1, d.getFullYear()]
    .map(n => String(n).padStart(2, '0')).join('.');
};

const MON_FULL_RU = ['января','февраля','марта','апреля','мая','июня','июля','августа','сентября','октября','ноября','декабря'];

/* Decorative 4-point star SVG used across the template as background sparkle. */
function Star({ className, style, size = 40 }) {
  return (
    <svg className={className} style={style} width={size} height={size} viewBox="0 0 100 100" aria-hidden="true" fill="none">
      <path d="M50 0 C 52 35, 65 48, 100 50 C 65 52, 52 65, 50 100 C 48 65, 35 52, 0 50 C 35 48, 48 35, 50 0 Z"
        fill="currentColor" />
    </svg>
  );
}

/* Load Unbounded display font once per session. */
function useUnboundedFont() {
  React.useEffect(() => {
    const id = 'amt-unbounded-font';
    if (document.getElementById(id)) return;
    const link = document.createElement('link');
    link.id = id;
    link.rel = 'stylesheet';
    link.href = 'https://fonts.googleapis.com/css2?family=Unbounded:wght@400;700;800&display=swap';
    document.head.appendChild(link);
  }, []);
}
const getDayMonth = raw => {
  if (!raw) return { day: '??', month: '—' };
  const d = new Date(raw);
  return { day: String(d.getDate()).padStart(2, '0'), month: MON_FULL_RU[d.getMonth()] };
};



/* ══ Main Template ═════════════════════════════════════════════ */
export default function Amethyst({ project, design, isPreview }) {
  const pageRef  = useRef(null);
  const blockDep = project?.blocks?.length;
  useReveal(pageRef, blockDep, { revealSelector: '.amt-reveal', inClass: 'amt-in' });
  useUnboundedFont();

  /* Detect when we're rendered inside the constructor's phone preview
     (`.lp-phone-screen`) so the fixed CTA can switch to sticky mode
     and stay inside the preview frame. */
  const [inPreview, setInPreview] = useState(!!isPreview);
  useEffect(() => {
    const root = pageRef.current;
    if (!root) return;
    setInPreview(!!isPreview || !!root.closest('.lp-phone-screen,.lp-template-inner,[class*="lp-"]'));
  }, [isPreview]);

  const p = project || {};
  const d = design  || {};

  /* ── Colors ── */
  const accent = d.palette?.primary   || p.palette?.primary   || '#250061';
  const bg     = d.palette?.secondary || p.palette?.secondary || '#e4e2ff';
  const fg     = d.palette?.textDark  || p.palette?.textDark  || '#250061';

  /* ── Blocks ── */
  const blocks = p.blocks || ['menu','map','schedule','palette','rsvp','survey','message','wishlist','countdown','calendar'];
  const HAS_MENU     = blocks.includes('menu');
  const HAS_CALENDAR = blocks.includes('calendar');

  /* ── Text ── */
  const bride   = p.bride || '';
  const groom   = p.groom || '';
  const tagline     = p.block_content?.header?.subtitle || '';
  const footerQuote = p.block_content?.footer?.quote    || '';
  const invText = p.invitation_text || p.block_content?.message?.text || '';

  /* ── Photos ── */
  const photos   = p.cover_photos || (p.cover_photo ? [{ url: p.cover_photo }] : []);
  const photoA   = src(photos[0]?.url) || src(d.photos?.main) || null;
  const photoB   = src(photos[1]?.url) || null;
  const dcPhotos = p.dresscode_photos || [];
  const footerPhotoObj = p.footer_photo?.url ? p.footer_photo : null;
  const footerPhoto    = src(footerPhotoObj?.url) || null;

  /* ── Date ── */
  const dateStr = fmtDateDots(p.wedding_date);
  const { day: bigDay, month: bigMonth } = getDayMonth(p.wedding_date);

  /* ── Ticker ── */
  /* makeItems → now handled by <TickerBar>. */

  /* Schedule comes from project; server merges admin defaults if empty. */
  const schedule = p.schedule?.length ? p.schedule : [];

  /* ── Venue ── */
  const venues  = p.venues || [];

  /* ── Dresscode ── */
  const swatches = p.dresscode_palette || [];

  /* ── Wishlist ── */
  const wishlist = p.wishlist || [];

  /* ── Block content ── */
  const bc = p.block_content || {};
  const titleSchedule = bc.schedule?.title  || '';
  const titleLocation = bc.map?.title       || '';
  const titleDress    = bc.palette?.title   || '';
  const titleRsvp     = bc.rsvp?.title      || '';
  const titleWish     = bc.wishlist?.title  || '';
  const titleCD       = bc.countdown?.title || '';
  /* Subtitles — always from DB (block_content). Empty = no subtitle. */
  /* Subtitles render via <BlockSubtitle> below — no inline fallback copy
     here; defaults live in server admin DEFAULT_SETTINGS.block_content. */
  const calBtn        = bc.calendar?.title || '';

  return (
    <div className={`amt-page${inPreview ? ' amt-page--preview' : ''}`} ref={pageRef}
      style={{ '--amt-accent': accent, '--amt-bg': bg, '--amt-fg': fg }}>
      <PaidWatermark isPaid={project?.is_paid} isPreview={inPreview} />

      {/* ── Ticker ───────────────────────────────────────────────── */}
      <TickerBar text={dateStr} classes={AMT_TICKER_CLASSES} />

      {/* ── Menu / Navigation (Figma: above hero) ────────────────── */}
      {HAS_MENU && (
        <nav className="amt-nav amt-pad">
          {blocks.includes('schedule') && <a href="#amt-schedule">{bc.menu?.schedule || 'Программа'}</a>}
          {blocks.includes('map')      && <a href="#amt-location">{bc.menu?.location || 'Локация'}</a>}
          {blocks.includes('palette')  && <a href="#amt-dresscode">{bc.menu?.dresscode || 'Дресс-код'}</a>}
          {blocks.includes('rsvp')     && <a href="#amt-rsvp">{bc.menu?.rsvp || 'Анкета'}</a>}
        </nav>
      )}

      {/* ── Hero per Figma 12833:2411 (390×507, locket 570×727 @ -90.3/-233) */}
      <section className="amt-hero amt-reveal">
        {/* Locket bleeds behind content */}
        <img className="amt-hero-locket" src="/templates/amethyst/locket.png" alt="" aria-hidden="true" />

        {/* Heart cut-out photos, positioned in hero coords */}
        {photoA && (
          <div className="amt-hero-photo amt-hero-photo--a"
            style={{
              WebkitMaskImage: "url('/templates/amethyst/heart-mask-a.png')",
              maskImage:       "url('/templates/amethyst/heart-mask-a.png')",
            }}>
            <img src={photoA} alt="" style={{ filter: flt(photos[0]), objectPosition: objPos(photos[0]) }} />
          </div>
        )}
        {photoB && (
          <div className="amt-hero-photo amt-hero-photo--b"
            style={{
              WebkitMaskImage: "url('/templates/amethyst/heart-mask-b.png')",
              maskImage:       "url('/templates/amethyst/heart-mask-b.png')",
            }}>
            <img src={photoB} alt="" style={{ filter: flt(photos[1]), objectPosition: objPos(photos[1]) }} />
          </div>
        )}

        <Star className="amt-star amt-star--1" size={34} />
        <Star className="amt-star amt-star--2" size={34} />
        <Star className="amt-star amt-star--3" size={84} />
        <Star className="amt-star amt-star--4" size={84} />

        {/* Names + tagline — top region */}
        <div className="amt-hero-head">
          <div className="amt-hero-names">
            <div className="amt-name-tile amt-name-tile--a">
              <span className="amt-name">{bride}</span>
            </div>
            <div className="amt-name-tile amt-name-tile--amp">
              <span className="amt-name-amp">{bc.header?.amp || 'и'}</span>
            </div>
            <div className="amt-name-tile amt-name-tile--b">
              <span className="amt-name">{groom}</span>
            </div>
          </div>
          {tagline && <p className="amt-tagline">{tagline}</p>}
        </div>

        {/* Date tiles — bottom region, 141px gap from names (Figma) */}
        <div className="amt-hero-date">
          <div className="amt-date-tile amt-date-tile--a">
            <span className="amt-date-num">{bigDay}</span>
          </div>
          <div className="amt-date-tile amt-date-tile--b">
            <span className="amt-date-mon">{bigMonth}</span>
          </div>
        </div>
      </section>

      {/* ── Invitation letter ────────────────────────────────────── */}
      <section className="amt-pad amt-invite amt-reveal">
        <Star className="amt-star amt-star--inv1" size={68} />
        <Star className="amt-star amt-star--inv2" size={77} />
        <div className="amt-h-tile amt-h-tile--a">
          <h2 className="amt-h">{bc.message?.title || 'Дорогие наши!'}</h2>
        </div>
        <div className="amt-invite-body">
          {invText
            ? invText.split('\n').map((line, i) =>
                line.trim() ? <p key={i}>{line}</p> : <br key={i} />
              )
            : null
          }
        </div>
      </section>

      {/* ── Date tiles + Calendar button ─────────────────────────── */}
      <section className="amt-pad amt-date-sect amt-reveal">
        <div className="amt-date-wrap">
          {/* Обводка-каракуля. Inline-SVG (а не img), чтобы цвет брался из
              акцента палитры (currentColor), как звёзды на фоне. */}
          <svg className="amt-date-scribble" viewBox="0 0 387 275" fill="none"
               preserveAspectRatio="none" aria-hidden="true">
            <g opacity="0.3" clipPath="url(#amtScribbleClip)">
              <path d="M282.258 158.714C284.688 160.745 287.46 162.619 289.702 165.025C308.389 184.896 307.486 210.39 287.055 228.386C281.386 233.385 274.97 237.915 268.212 241.289C220.84 264.909 171.288 269.345 120.085 257.129C109.34 254.567 99.5603 249.631 90.9642 242.476C71.4672 226.324 65.7365 201.704 76.0456 178.584C81.5894 166.15 90.1544 156.027 100.463 147.467C136.249 117.817 177.268 104.851 223.518 109.663C239.527 111.318 254.695 116.442 268.025 125.721C273.694 129.658 278.957 134.438 283.442 139.687C289.453 146.748 288.955 150.466 282.227 158.714H282.258ZM273.195 151.028L274.472 149.529C272.385 147.248 270.454 144.78 268.181 142.718C259.367 134.688 248.808 129.658 237.347 127.221C184.462 115.849 138.928 130.127 100.681 167.993C95.854 172.773 92.1165 179.116 89.2823 185.395C82.2123 200.954 85.14 215.264 96.6949 227.761C102.955 234.54 110.741 239.321 119.493 241.414C132.014 244.413 144.752 247.319 157.553 248.287C191.501 250.849 224.515 245.913 255.536 231.26C263.82 227.355 271.825 222.106 278.677 215.982C290.823 205.141 291.82 190.019 281.075 177.772C275.5 171.399 268.337 166.025 261.017 161.713C238.78 148.685 214.611 144.373 189.072 147.56C186.456 147.873 183.559 148.56 181.254 147.779C179.136 147.06 176.489 144.748 176.147 142.811C175.804 140.843 177.486 137.531 179.292 136.407C181.971 134.719 185.459 134.126 188.698 133.532C215.701 128.44 240.711 134.907 264.724 146.904C267.527 148.31 270.361 149.685 273.195 151.059V151.028Z" fill="currentColor" />
              <path d="M335.797 130.377C341.217 131.439 346.605 132.47 352.024 133.563C355.606 134.282 359.219 134.844 362.676 135.907C366.195 137 368.188 139.656 367.69 143.499C367.192 147.31 364.762 149.435 360.931 149.404C357.973 149.404 354.983 149.091 352.055 148.623C340.282 146.748 328.572 144.655 316.799 142.843C306.77 141.312 303.531 136.063 308.016 126.784C313.622 115.13 319.82 103.758 325.893 92.354C327.762 88.8548 330.721 86.3866 334.832 88.4487C338.725 90.3857 339.192 93.8849 337.573 97.8215C335.953 101.758 334.551 105.788 332.402 111.443C335.548 110.038 337.541 109.413 339.223 108.35C349.75 101.446 360.215 94.4161 370.711 87.4489C372.642 86.1679 374.511 84.6683 376.629 83.8872C380.273 82.5438 384.104 82.45 386.19 86.4491C388.277 90.4482 386.159 93.1039 382.671 95.4471C368.936 104.757 355.388 114.255 341.777 123.722C339.659 125.19 337.573 126.752 335.486 128.283C335.61 129.002 335.735 129.689 335.828 130.408L335.797 130.377Z" fill="currentColor" />
              <path d="M87.141 106.472C85.7958 103.205 84.4107 99.957 83.1455 96.6527C81.6921 92.9493 80.1001 89.2621 79.0146 85.4597C77.975 81.8064 79.4379 78.8577 82.9816 77.3223C86.6346 75.7601 89.4946 77.2229 91.5755 80.2919C92.683 81.9168 93.4685 83.7897 94.315 85.5853C100.289 98.2052 106.223 110.844 112.238 123.445C117.066 133.532 113.943 139.425 102.953 138.769C90.7001 138.022 78.4966 136.034 66.3073 134.283C62.5459 133.753 59.1909 131.913 59.5111 127.161C59.7938 123.157 62.51 121.522 68.4235 121.703C71.3162 121.786 74.1772 121.958 79.1998 122.215C76.3432 119.363 75.0593 117.674 73.3983 116.478C64.2291 109.857 54.9482 103.362 45.7178 96.8181C43.027 94.8865 40.0941 93.1656 37.7782 90.8396C35.3176 88.3621 34.063 85.0284 36.5097 81.8388C39.2832 78.2042 42.759 78.6973 46.2009 81.0324C56.4011 87.9261 66.6012 94.8199 76.8013 101.714C79.7378 103.701 82.7143 105.669 85.6507 107.656C86.1244 107.264 86.598 106.872 87.101 106.491L87.141 106.472Z" fill="currentColor" />
              <path d="M337.012 242.945C339.535 249.943 341.497 255.223 343.303 260.566C344.144 263.065 345.047 265.627 345.359 268.22C345.732 271.407 344.642 274.813 341.123 275C338.6 275.156 335.673 273.657 333.492 272.063C332.122 271.063 331.748 268.595 331.063 266.721C327.637 257.348 324.211 248.006 320.879 238.602C317.639 229.542 320.972 222.168 330.129 220.325C341.154 218.107 352.366 216.919 363.516 215.389C365.136 215.17 367.192 214.795 368.406 215.545C370.711 216.982 373.607 218.825 374.386 221.106C375.601 224.793 372.86 227.667 369.309 228.323C364.139 229.292 358.845 229.417 351.899 230.041C354.329 231.978 355.512 233.228 356.945 233.978C364.638 237.852 372.424 241.539 380.117 245.444C382.141 246.475 384.508 247.662 385.661 249.443C386.751 251.13 387.249 254.13 386.439 255.817C385.661 257.441 382.889 259.097 381.051 259.035C378.217 258.91 375.414 257.504 372.704 256.348C363.236 252.38 353.83 248.287 344.362 244.351C342.649 243.632 340.687 243.601 337.074 242.882L337.012 242.945Z" fill="currentColor" />
            </g>
            <defs><clipPath id="amtScribbleClip"><rect width="387" height="275" fill="white" /></clipPath></defs>
          </svg>
          <DateCards date={p.wedding_date} classes={AMT_DATECARDS_CLASSES} />
        </div>
        {HAS_CALENDAR && (
          <CalendarButton
            bride={bride}
            groom={groom}
            date={p.wedding_date}
            time={p.wedding_time}
            venues={p.venues}
            address={p.wedding_address}
            className="amt-btn amt-btn--primary amt-btn--full">
            {calBtn}
          </CalendarButton>
        )}
      </section>

      {/* ── Dynamic blocks — force Figma order regardless of incoming
              blocks array; include only blocks that are present. ─── */}
      {['map','schedule','palette','rsvp','wishlist','countdown']
        .filter(b => blocks.includes(b))
        .map(blockId => {
        switch (blockId) {
          case 'map':
            return (
              <section key="map" className="amt-pad amt-map-sect amt-reveal" id="amt-location">
                <Star className="amt-star amt-star--map1" size={77} />
                <Star className="amt-star amt-star--map2" size={42} />
                <div className="amt-h-tile amt-h-tile--b">
                  <h2 className="amt-h">{titleLocation}</h2>
                </div>
                <BlockSubtitle text={bc.map?.subtitle} className="amt-sub" />
                <Star className="amt-star amt-star--loc" size={60} />

                {venues.map((v, i) => (
                  <div key={i} className="amt-addr">
                    {(v.address || '').trim() && (
                    <span className="amt-pin" aria-hidden="true">
                      <svg viewBox="0 0 24 24" fill="none">
                        <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z"
                          fill="currentColor" />
                      </svg>
                    </span>
                    )}
                    <span className="amt-addr-text">
                      {v.name && v.address ? `${v.name}, ${v.address}` : (v.name || v.address)}
                    </span>
                  </div>
                ))}
                {!venues.some(v => v.address) && p.wedding_address && (
                  <div className="amt-addr">
                    <span className="amt-pin" aria-hidden="true">
                      <svg viewBox="0 0 24 24" fill="none">
                        <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z"
                          fill="currentColor" />
                      </svg>
                    </span>
                    <span className="amt-addr-text">{p.wedding_address}</span>
                  </div>
                )}

                {/* Карта — только при наличии адреса (имя без адреса → без карты). */}
                  {(p.map_url || (p.wedding_address || '').trim() || venues.some(v => (v.address || '').trim())) && (
                  <div className="amt-map-wrap">
                  {(() => {
                    if (p.map_url) {
                      return <MapFrame region={p.region} provider={bc.map?.provider} title="Карта" src={p.map_url} allowFullScreen style={{width:'100%',height:'100%',border:'none'}} />;
                    }
                    const withCoords = venues.filter(v => Number.isFinite(+v.lat) && Number.isFinite(+v.lon));
                    if (withCoords.length > 0) {
                      /* Use Yandex JS API: we control the map fully,
                         drop a pin per venue and fit the bounds — no
                         auto-routing between points. */
                      return <YandexMap points={withCoords} singleZoom={16} region={p.region} provider={bc.map?.provider} />;
                    }
                    const addr = venues.length > 0
                      ? venues.map(v=>v.address).filter(Boolean).join(' — ')
                      : (p.wedding_address || '');
                    if (!addr) {
                      return <MapFrame region={p.region} provider={bc.map?.provider} title="Карта"
                        src="https://yandex.ru/map-widget/v1/?ll=37.617617,55.755811&z=12&lang=ru_RU"
                        allowFullScreen style={{width:'100%',height:'100%',border:'none'}} allow="geolocation" />;
                    }
                    const mapSrc = venues.length > 1
                      ? `https://yandex.ru/map-widget/v1/?rtext=${venues.map(v=>encodeURIComponent(v.address)).join('~')}&rtt=auto&lang=ru_RU`
                      /* mode=search forces Yandex to drop a pin on the first
                         match instead of just centering the map. */
                      : `https://yandex.ru/map-widget/v1/?mode=search&text=${encodeURIComponent(addr)}&z=16&lang=ru_RU`;
                    return <MapFrame region={p.region} provider={bc.map?.provider} title="Карта" src={mapSrc} allowFullScreen style={{width:'100%',height:'100%',border:'none'}} allow="geolocation" />;
                  })()}
                </div>
                  )}
              </section>
            );

          case 'schedule':
            return (
              <section key="schedule" className="amt-pad amt-sched-sect amt-reveal" id="amt-schedule">
                <Star className="amt-star amt-star--sch1" size={77} />
                <Star className="amt-star amt-star--sch2" size={28} />
                <div className="amt-h-tile amt-h-tile--c">
                  <h2 className="amt-h">{titleSchedule}</h2>
                </div>
                <BlockSubtitle text={bc.schedule?.subtitle} className="amt-sub" />
                <Schedule items={schedule} classes={AMT_SCHED_CLASSES} />
              </section>
            );

          case 'palette':
            return (
              <section key="palette" className="amt-pad amt-dress-sect amt-reveal" id="amt-dresscode">
                <Star className="amt-star amt-star--dr1" size={43} />
                <div className="amt-h-tile amt-h-tile--d">
                  <h2 className="amt-h">{titleDress}</h2>
                </div>
                <BlockSubtitle text={bc.palette?.subtitle} className="amt-sub" />
                <DresscodePalette
                  swatches={swatches}
                  photos={dcPhotos}
                  cssFilter={flt}
                  classes={AMT_DRESS_CLASSES}
                />
              </section>
            );

          case 'rsvp':
            return (
              <section key="rsvp" className="amt-pad amt-rsvp-sect amt-reveal" id="amt-rsvp">
                <Star className="amt-star amt-star--rs1" size={28} />
                <div className="amt-h-tile amt-h-tile--e">
                  <h2 className="amt-h amt-h--wrap">{titleRsvp}</h2>
                </div>
                <BlockSubtitle text={bc.rsvp?.subtitle} className="amt-sub" />
                <RSVPForm project={p} blocks={blocks} classes={AMT_RSVP_CLASSES} />
              </section>
            );

          case 'wishlist':
            return (
              <section key="wishlist" className="amt-pad amt-wish-sect amt-reveal">
                <Star className="amt-star amt-star--ws1" size={43} />
                <div className="amt-h-tile amt-h-tile--f">
                  <h2 className="amt-h">{titleWish}</h2>
                </div>
                <BlockSubtitle text={bc.wishlist?.subtitle} className="amt-sub" />
                <Wishlist
                  items={wishlist} currency={bc.wishlist?.currency} reserveText={bc.wishlist?.reserveText}
                  slug={p.slug}
                  classes={AMT_WISHLIST_CLASSES}
                  modalClasses={AMT_WISHLIST_MODAL_CLASSES}
                />
                {(!wishlist.length) && p.wishlist_url && (
                  <a href={p.wishlist_url} target="_blank" rel="noopener noreferrer"
                    className="amt-btn amt-btn--primary amt-btn--full">
                    Перейти к виш‑листу
                  </a>
                )}
              </section>
            );

          case 'countdown':
            return (
              <section key="countdown" className="amt-pad amt-cd-sect amt-reveal">
                <Star className="amt-star amt-star--cd1" size={58} />
                <Star className="amt-star amt-star--cd2" size={31} />
                <div className="amt-h-tile amt-h-tile--g">
                  <h2 className="amt-h amt-h--wrap">{titleCD}</h2>
                </div>
                <BlockSubtitle text={bc.countdown?.subtitle} className="amt-sub" />
                <Countdown date={p.wedding_date} time={p.wedding_time} bc={bc} classes={AMT_CD_CLASSES} />
              </section>
            );

          default:
            return null;
        }
      })}

      {/* ── Footer — photo + quote ───────────────────────────────── */}
      <footer className="amt-footer amt-reveal">
        <Star className="amt-star amt-star--ft1" size={58} />
        <Star className="amt-star amt-star--ft2" size={31} />
        <Star className="amt-star amt-star--ft3" size={22} />
        {footerPhoto && (
          <div className="amt-footer-photo">
            <img src={footerPhoto} alt="" style={{ filter: flt(footerPhotoObj), objectPosition: objPos(footerPhotoObj) }} />
          </div>
        )}
        <div className="amt-footer-quote">
          <p>{footerQuote.split('\n').map((line, i, arr) => (
            <React.Fragment key={i}>
              {line}
              {i < arr.length - 1 && <br />}
            </React.Fragment>
          ))}</p>
        </div>
      </footer>

      {/* ── Sticky RSVP CTA (position:fixed to viewport) ────────── */}
      {blocks.includes('rsvp') && (
        <FixedCTA
          href="#amt-rsvp"
          targetId="amt-rsvp"
          scrollRoot={pageRef}
          className="amt-fixed-cta">
          {bc.rsvp?.ctaText || 'Подтвердить присутствие'}
        </FixedCTA>
      )}

      {(project?.blocks || []).includes('music') && project?.music?.track?.url && (
        <MusicPlayer music={project.music} palette={project.palette} isPreview={inPreview} />
      )}
    </div>
  );
}
